import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { mergeMap, map, switchMap, catchError } from 'rxjs/operators';
import { combineLatest, from, of } from 'rxjs';
import { AuthenticationService } from '@nx-fe/services';
import { DidRestClientService } from 'core/services';

@Injectable()
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private restSvc: DidRestClientService,
    private authenticationService: AuthenticationService
  ) {}

  canActivate() {
    return from(this.authenticationService.isAuthenticated()).pipe(
      switchMap(authDetails => {
        if (!authDetails.isAuth) {
          return of(false);
        }
      }),
      catchError(_ => {
        const loginUrl$ = from(this.authenticationService.getLoginUrlAsync());
        return combineLatest([this.restSvc.getSessionState(), loginUrl$]).pipe(
          map(([state, loginUrl]) => {
            const user = state.user;
            const tenant = state.tenant;

            const activeSession = this.authenticationService.isActiveSession;
            const expiredSession = this.authenticationService.isExpiredSession;

            if (!user.authenticated) {
              this.authenticationService.isActiveSession = false;

              if (activeSession) {
                // an active session has become expired, so redirect to expired session splash page
                if (!expiredSession && user.expired) {
                  this.authenticationService.isExpiredSession = true;
                  // Redirect to expired session UI only if we are not on signOut route already
                  // in this case we just want to stay on this rout and show signOut component
                  if (!this.authenticationService.logOutInProgress) {
                    this.router.navigate(['/expiredSession'], {
                      queryParams: { redirectUrl: loginUrl },
                    });
                  }
                }
              } else {
                if (!this.authenticationService.logOutInProgress) {
                  window.location.href = loginUrl;
                }
              }
              return of(false);
            }

            return of(tenant).pipe(
              map(status => {
                if (!status || !status.exists || !status.active) {
                  this.router.navigate(['/notFound']);

                  return false;
                }

                return true;
              }),
              map(tenantValid => {
                if (tenantValid) {
                  this.authenticationService.isActiveSession = true;
                  this.authenticationService.isExpiredSession = false;
                }
                return tenantValid;
              })
            );
          }),
          mergeMap(result => result)
        );
      })
    );
  }
}
